import { ApproverTypeEnum, CurrencyEnum, Employee, LogicalOperatorEnum, WorkflowConditionEnum } from "@finway-group/shared/lib/models"
import { Button, Col, Row } from "antd"
import React, { useCallback } from "react"
import { Trash2 as TrashIcon } from "react-feather"
import { useTranslation } from "react-i18next"

import { SwitchToogleButton } from "Components/actionButton/switchToogleButton"
import CurrencyInput from "Components/currencyInput"
import FormItemCurrency from "Components/form/formItemCurrency"
import { useEmployees, useLoggedInEmployee } from "Shared/hooks/employee.hooks"
import { EmployeeService, UserService } from "Shared/services"
import { getPopupAnchor } from "Shared/utils/popup.utils"

import { CurrencySelect } from "../expenseForm/commonInputFields/CurrencySelect"
import { WorkflowFormResourceTypeEnum, WorkflowTriggerObjectSelect } from "./triggerObjectSelect"
import { WorkflowFormTypeEnum, WorkflowFormValuesInterface } from "./workflowFormValues.interface"

interface WorkflowFormStepInterface {
    stepIndex: number
    step: WorkflowFormValuesInterface["steps"][0]
    onRemove: (index: number | Array<number>) => void
    onUpdate: (index: number, values: WorkflowFormValuesInterface["steps"][0]) => void
    isAutoApproverSelected: boolean
    anchorRef: React.RefObject<HTMLDivElement>
    rules: any
    workflowType: WorkflowFormTypeEnum
}

export const WorkflowFormStep = ({ stepIndex, step, onRemove, onUpdate, isAutoApproverSelected, anchorRef, rules, workflowType }: WorkflowFormStepInterface) => {
    const { t } = useTranslation()
    const loggedInEmployee = useLoggedInEmployee()
    const autoApprover = EmployeeService.getAutoApprover()
    const employees = useEmployees()

    const possibleApprovers = UserService.getApproverEmployeeList(employees)

    const handleNetAmountConditionClick = useCallback(() => {
        onUpdate(stepIndex, {
            ...step,
            condition: WorkflowConditionEnum.NET_AMOUNT,
            currency: loggedInEmployee.settings.globalCurrency ?? CurrencyEnum.EUR,
        })
    }, [stepIndex, step, onUpdate])

    const handleRemoveCondition = useCallback(() => {
        onUpdate(stepIndex, { ...step, condition: undefined })
    }, [stepIndex, step, onUpdate])

    const handleRemoveStep = useCallback(() => {
        onRemove(stepIndex)
    }, [stepIndex, onRemove])

    const handleSelectApprover = useCallback(
        (approver: string) => {
            onUpdate(stepIndex, { ...step, approver })
        },
        [stepIndex, step, onUpdate],
    )

    const handleSelectOtherApprovers = useCallback(
        (otherApprovers: Array<string>) => {
            onUpdate(stepIndex, { ...step, otherApprovers })
        },
        [stepIndex, step, onUpdate],
    )

    const renderNetAmountButton = () => {
        const isTheAutoApprover = step.approver === ApproverTypeEnum.AUTO_APPROVER
        const shouldRenderNetAmountButton = workflowType !== WorkflowFormTypeEnum.FOLDER && !step.condition && !isTheAutoApprover

        if (!shouldRenderNetAmountButton) {
            return null
        }

        return (
            <Button className="h-32 mt-6" onClick={handleNetAmountConditionClick}>
                {">€"}
            </Button>
        )
    }

    const renderRemoveConditionButton = () => {
        // If the auto approver is selected, the first step has no condition and the
        // second step's condition must not be removed. So we don't render the remove button.
        if (isAutoApproverSelected && stepIndex < 2) {
            return null
        }

        return (
            <Button className="h-32 mt-6" onClick={handleRemoveCondition}>
                <TrashIcon />
            </Button>
        )
    }

    const renderRemoveStepButton = () => {
        if (stepIndex === 0) {
            return null
        }

        return (
            <Button className="h-32 mt-6" onClick={handleRemoveStep}>
                <TrashIcon />
            </Button>
        )
    }

    const handleAndOrSwitchClick = useCallback(
        (selectedButtons: { button1: boolean; button2: boolean }) => {
            const updatedStep = { ...step }

            const removeOperator = () => {
                updatedStep.operator = undefined
                updatedStep.otherApprovers = []
            }

            if (selectedButtons.button1) {
                if (step.operator === LogicalOperatorEnum.AND) {
                    removeOperator()
                } else {
                    updatedStep.operator = LogicalOperatorEnum.AND
                }
            } else if (selectedButtons.button2) {
                if (step.operator === LogicalOperatorEnum.OR) {
                    removeOperator()
                } else {
                    updatedStep.operator = LogicalOperatorEnum.OR
                }
            } else {
                removeOperator()
            }
            onUpdate(stepIndex, updatedStep)
        },
        [stepIndex, onUpdate],
    )

    const isTheAutoApprover = (approver: Employee) => approver.id === autoApprover.id
    const isDeleted = (approver: Employee) => approver.activeCompanyProfile.deleted
    const currentlySelectedApprover = step.approver
    const isAlreadyInTheApproverDropdown = (approver: Employee) => approver.id === currentlySelectedApprover
    const isAlreadyInTheOtherApproversDropdown = (approver: Employee) => step.otherApprovers.includes(approver.id)
    const approverSelectOptions = possibleApprovers.filter(
        (approver) =>
            !isTheAutoApprover(approver) && // Select approvers that are not the auto approver, ...
            !isAlreadyInTheOtherApproversDropdown(approver) && // ... that are not already in the other dropdown  ...
            (!isDeleted(approver) || isAlreadyInTheApproverDropdown(approver)), // ... and that are not deleted, unless the approver was already in the form. (So we don't show the mongo string id)
    )

    // add the currently selected approver to the approverSelectOptions if it is not already in there, so we don't show a doc id
    if (
        !!currentlySelectedApprover &&
        !(Object.values(ApproverTypeEnum) as Array<string>).includes(currentlySelectedApprover) &&
        !approverSelectOptions.filter((selectOption) => selectOption.id === currentlySelectedApprover).length
    ) {
        approverSelectOptions.push(EmployeeService.getEmployeeById(currentlySelectedApprover))
    }

    const otherApproversSelectOptions = possibleApprovers.filter(
        (approver) => !isTheAutoApprover(approver) && !isAlreadyInTheApproverDropdown(approver) && (!isDeleted(approver) || isAlreadyInTheOtherApproversDropdown(approver)),
    )

    return (
        <>
            {step.condition === WorkflowConditionEnum.NET_AMOUNT && (
                <Row align="middle" className="w-full" gutter={[8, 0]}>
                    <Col span={5}>
                        <FormItemCurrency
                            name={[stepIndex, "amount"]}
                            label={t("input:workflow.condition.net_amount_bigger")}
                            required
                            rules={rules.step.amount}
                            currency={step.currency || loggedInEmployee.settings.globalCurrency}
                        >
                            <CurrencyInput
                                className="w-full react-currency-input"
                                isNegativeAllowed={false}
                                value={0}
                                currency={step.currency || loggedInEmployee.settings.globalCurrency}
                            />
                        </FormItemCurrency>
                    </Col>
                    <Col span={4}>
                        <CurrencySelect
                            rules={rules.step.currency}
                            name={[stepIndex, "currency"]}
                            initialValue={step.currency || loggedInEmployee.settings.globalCurrency}
                            size="small"
                            anchor={anchorRef.current}
                        />
                    </Col>
                    <Col span={2}>{renderRemoveConditionButton()}</Col>
                </Row>
            )}

            <Row align="middle" className="w-full" gutter={[8, 8]}>
                <Col span={9} data-testid="finway:workflowStep:selectApprover">
                    <WorkflowTriggerObjectSelect
                        name={[stepIndex, "approver"]}
                        label={t("input:workflow.approver")}
                        required={true}
                        resources={approverSelectOptions}
                        resourceType={WorkflowFormResourceTypeEnum.EMPLOYEE}
                        selectedResourcesIds={[step.approver]}
                        includeAutoApprover={stepIndex === 0}
                        includeSuperior={!step.otherApprovers.includes(ApproverTypeEnum.SUPERIOR)}
                        includeResponsibleEmployee={!step.otherApprovers.includes(ApproverTypeEnum.RESPONSIBLE_USER)}
                        includeRequester={!step.otherApprovers.includes(ApproverTypeEnum.REQUESTER)}
                        anchor={getPopupAnchor(anchorRef.current)}
                        onSelect={handleSelectApprover}
                        rules={rules.step.approver}
                    />
                </Col>
                {step.approver !== ApproverTypeEnum.AUTO_APPROVER && (
                    <Col span={3} className="mt-6">
                        <SwitchToogleButton
                            value={{
                                button1: step.operator === LogicalOperatorEnum.AND,
                                button2: step.operator === LogicalOperatorEnum.OR,
                            }}
                            onClick={handleAndOrSwitchClick}
                            label1={t("input:workflow.operator.and")}
                            label2={t("input:workflow.operator.or")}
                        />
                    </Col>
                )}
                {step.operator && [LogicalOperatorEnum.AND, LogicalOperatorEnum.OR].includes(step.operator) && (!isAutoApproverSelected || stepIndex !== 0) && (
                    <Col span={9} data-testid="finway:workflow-step:selectApproverAndOr-btn">
                        <WorkflowTriggerObjectSelect
                            name={[stepIndex, "otherApprovers"]}
                            label={t("input:workflow.approver")}
                            required={true}
                            isMultiple
                            resources={otherApproversSelectOptions}
                            resourceType={WorkflowFormResourceTypeEnum.EMPLOYEE}
                            selectedResourcesIds={step.otherApprovers}
                            includeSuperior={step.approver !== ApproverTypeEnum.SUPERIOR}
                            includeResponsibleEmployee={step.approver !== ApproverTypeEnum.RESPONSIBLE_USER}
                            includeRequester={step.approver !== ApproverTypeEnum.REQUESTER}
                            anchor={getPopupAnchor(anchorRef.current)}
                            onSelect={handleSelectOtherApprovers}
                            rules={rules.step.otherApprovers}
                            maxOptions={2}
                        />
                    </Col>
                )}
                <Col span={3} className="flex gap-4">
                    {renderNetAmountButton()}
                    {renderRemoveStepButton()}
                </Col>
            </Row>
        </>
    )
}
